import { css } from "@emotion/react";
import { gql, useMutation } from "@apollo/client";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as LockIcon } from "../../../../../images/icons/lock-icon.svg";
import { ReactComponent as MailIcon } from "../../../../../images/icons/mail-icon.svg";
import { AUTH_URL, getUrl } from "../../../../includes/routes";
import { FAILED_AUTHENTICATION_PARAM } from "../../../../includes/constants";
import { useZitadel, ZitadelSession, ZitadelTotpRegistration } from "../../../../contexts/ZitadelContext";
import Button from "../../../Button";
import { API_BASE_URL } from "../../../../../config/config";

type ZitadelStartTotpRegistrationMutationResponse = {
    zitadelStartTotpRegistration: {
        session: ZitadelSession;
        secret: ZitadelTotpRegistration;
    };
}

type ZitadelStartTotpRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelStartOtpEmailRegistrationMutationResponse = {
    zitadelStartOtpEmailRegistration: ZitadelSession;
}

type ZitadelStartOtpEmailRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelSkipMfaRegistrationMutationResponse = {
    zitadelSkipMfaRegistration: {
        id: string;
    };
}

type ZitadelSkipMfaRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

const MfaInitialisation = () => {
    const navigate = useNavigate();

    const hasAutoSelected = useRef(false);

    const [isTotpLoading, setIsTotpLoading] = useState(false);
    const [isOtpEmailLoading, setIsOtpEmailLoading] = useState(false);
    const [isSkipLoading, setIsSkipLoading] = useState(false);
    const [isCompletionNoticeVisible, setIsCompletionNoticeVisible] = useState(false);

    const {
        loginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
        setTotpRegistration,
        clearTotpRegistration,
    } = useZitadel();

    const [zitadelStartTotpRegistration] =
        useMutation<ZitadelStartTotpRegistrationMutationResponse, ZitadelStartTotpRegistrationMutationVariables>(
            ZITADEL_START_TOTP_REGISTRATION,
        );

    const [zitadelStartOtpEmailRegistration] =
        useMutation<ZitadelStartOtpEmailRegistrationMutationResponse, ZitadelStartOtpEmailRegistrationMutationVariables>(
            ZITADEL_START_OTP_EMAIL_REGISTRATION,
        );

    const [zitadelSkipMfaRegistration] =
        useMutation<ZitadelSkipMfaRegistrationMutationResponse, ZitadelSkipMfaRegistrationMutationVariables>(
            ZITADEL_SKIP_MFA_REGISTRATION,
        );

    const onSelectTotp = useCallback(() => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register authenticator app.");

        setIsTotpLoading(true);

        zitadelStartTotpRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("TOTP registration did not return a valid response.");

            setSession(data.zitadelStartTotpRegistration.session);
            setTotpRegistration(data.zitadelStartTotpRegistration.secret);
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }, [
        clearLoginInfo,
        clearSession,
        clearTotpRegistration, 
        navigate,
        session,
        setSession,
        setTotpRegistration,
        zitadelStartTotpRegistration,
    ]);

    const onSelectOtpEmail = useCallback(() => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register email authentication.");

        setIsOtpEmailLoading(true);

        zitadelStartOtpEmailRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration did not return a valid response.");

            setSession(data.zitadelStartOtpEmailRegistration);
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }, [clearLoginInfo, clearSession, navigate, session, setSession, zitadelStartOtpEmailRegistration]);

    const onSelectSkip = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to skip MFA registration.");

        setIsSkipLoading(true);

        zitadelSkipMfaRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            if (!loginInfo?.isRedirectDisabled) {
                clearLoginInfo();
                clearSession();
                clearTotpRegistration();

                let url = API_BASE_URL + "/auth/login-redirect";
                if (loginInfo?.backUrl) url += "?BackURL=" + loginInfo.backUrl;
                window.location.href = url;
            } else {
                setIsCompletionNoticeVisible(true);
            }
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    const isActionsDisabled = isTotpLoading || isOtpEmailLoading || isSkipLoading;
    const isOneMfaMethodAllowed = session?.metadata.isTotpAllowed !== session?.metadata.isOtpEmailAllowed;
    const isOneMfaMethodForced = isOneMfaMethodAllowed && !session?.metadata.isSkipAllowed;

    useEffect(function autoSelectForcedMfaMethod() {
        /** Skip if queries are in-flight */
        if (hasAutoSelected.current) return;

        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) return;

        if (isOneMfaMethodForced) {
            /** Auto select an MFA method  */
            hasAutoSelected.current = true;
            if (session.metadata.isTotpAllowed) onSelectTotp();
            else if (session.metadata.isOtpEmailAllowed) onSelectOtpEmail();
            else throw new Error("Failed to register an MFA method.");
        }
    }, [session, isOneMfaMethodForced, onSelectOtpEmail, onSelectTotp]);

    /** Ensure nothing is rendered in this condition */
    if (isOneMfaMethodForced) return null;

    /** Completion notice overrides all */
    if (isCompletionNoticeVisible) return <div>Authentication complete, you may now close this window</div>;

    return (
        <div css={rootStyle}>
            <h1>Multi-Factor Authentication</h1>
            <p>
                {
                    "To enable the best level of security for your account, please select an authentication method " +
                    "below.  You will use this method each time you log in."
                }
            </p>
            <div css={mfaOptionsStyle}>
                {session?.metadata.isTotpAllowed && (
                    <button
                        css={mfaOptionStyle}
                        aria-label="Select authenticator app"
                        onClick={onSelectTotp}
                    >
                        <LockIcon />
                        <span>Authenticator app</span>
                    </button>
                )}
                {session?.metadata.isOtpEmailAllowed && (
                    <button
                        css={mfaOptionStyle}
                        aria-label="Select your email address"
                        onClick={onSelectOtpEmail}
                    >
                        <MailIcon />
                        <span>Your email address</span>
                    </button>
                )}
            </div>
            {session?.metadata.isSkipAllowed && (
                <Fragment>
                    <hr />
                    <p>
                        {
                            "Alternatively, you can skip this setup. Without multi-factor authentication, your " +
                            "password will be the only thing protecting your account."
                        }
                    </p>
                    <Button
                        css={skipButtonStyle}
                        type="submit"
                        forceLoading={isSkipLoading}
                        disabled={isActionsDisabled}
                        onClick={onSelectSkip}
                        useOutline
                    >
                        Skip
                    </Button>
                </Fragment>
            )}
        </div>
    );
};

const ZITADEL_START_TOTP_REGISTRATION = gql`
    mutation zitadelStartTotpRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelStartTotpRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            session {
                id
                token
                metadata {
                    userEmail
                    isMfaRegistrationRequired
                    isTotpChallengeRequired
                    isOtpEmailChallengeRequired
                    isTotpAllowed
                    isOtpEmailAllowed
                    isSkipAllowed
                }
            }
            secret {
                secret
                uri
            }
        }
    }
`;

const ZITADEL_START_OTP_EMAIL_REGISTRATION = gql`
    mutation zitadelStartOtpEmailRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelStartOtpEmailRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

const ZITADEL_SKIP_MFA_REGISTRATION = gql`
    mutation zitadelSkipMfaRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelSkipMfaRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
        }
    }
`;

const rootStyle = (theme: EmotionTheme) => css`
    /** Heavily derived from SignIn.tsx::authContentStyle */
    margin-top: 40px;
    width: 510px;
    position: relative;
    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }
    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }

    /** Heavily derived from SignIn.tsx::leftHeaderStyle */
    h1 {
        margin: 0;
        color: var(--primary-colour);
        font-weight: ${theme.fonts.weights.bold};

        ${theme.breakpoints.up("xxl")} {
            font-size: 50px;
            line-height: 75px;
        }

        ${theme.breakpoints.between("md", "xl")} {
            font-size: 43px;
            line-height: 65px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 30px;
            line-height: 45px;
        }
    }

    p {
        margin:  0;
    }

    hr {
        margin: 0;
        width: 100%;
    }

    --primary-colour: ${theme.colours.curiousBlue};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    padding-bottom: 10rem;
`;

const skipButtonStyle = () => css`
    width: 150px;
    border: 1px solid black;
    background-color: transparent;
    color: black;

    &:hover {
        background-color: black;
        color: white;
    }

    /** Heavily derived from SignIn.tsx::formSubmitButtonStyle */
    border-radius: 45px;
    height: 40px;
    padding: 0;
`;

const mfaOptionsStyle = css`
    display: flex;
    flex-direction: row;
    gap: 3rem;
`;

const mfaOptionStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;

    padding: 0;

    border: none;

    font-size: 1rem;
    color: var(--primary-colour);
    white-space: nowrap;

    background-color: transparent;

    svg {
        --diameter: 10rem;

        width: var(--diameter);
        height: var(--diameter);
        border-radius: 50%;
        padding: 0.5rem;
    }

    &:hover, &:focus {
        cursor: pointer;
        text-decoration: underline;

        svg {
            outline: 1px solid var(--primary-colour);
        }
    }
`;

export default MfaInitialisation;
